/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Flex,
  Button,
  Picker,
  Item,
  TextField,
  Image,
  ProgressCircle,
} from '@adobe/react-spectrum';
import { format } from 'date-fns';
import { EditorState, convertToRaw } from 'draft-js';
import fetchCustomFormsAndFields from '../../utils/commonFormUtils';

import RequestProgress from '../../components/RequestProgress';
import actionWebInvoke from '../../utils/actionCallUtils';
import actions from '../../config.json';
import OBJCODE from '../../constants';
import SystemField from '../../components/Forms/DynamicForms/systemFields';
import { Container, Title } from '../../components/Forms/DynamicForms/style';
import getHeaders from '../../utils/headers';

import { Wrap, ExistingBriefs, Actions, Submit } from '../Brief/styles';
import CustomField from '../../components/Forms/DynamicForms/customField';

const DynamicRequestForm = ({ ims, user }) => {
  const [requestProjectIdValue, setRequestProjectIdValue] = useState('');
  const [queueTopic, setQueueTopic] = useState({
    isLoading: true,
    elements: [],
  });
  const [queueId, setQueueId] = useState();
  const [values, setValues] = useState({});
  const [dynamicValues, setDynamicValues] = useState({});
  const [dateValues, setDateValues] = useState({});
  const [selectedQTopicData, setSelectedQTopicData] = useState();
  const [dateObject, setDateObject] = useState({});
  const [customFields, setCustomFields] = useState();
  const [convertedContent, setConvertedContent] = useState(null);
  const [multipleValues, setMultipleValues] = useState();
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [isFileComponent, setFileComponent] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const convertContentToHTML = name => {
    const currentContentAsHTML = JSON.stringify(
      convertToRaw(editorState[name].getCurrentContent() || ''),
    );
    setConvertedContent({
      ...convertedContent,
      [name]: currentContentAsHTML,
    });
  };

  const handleEditorChange = (state, name) => {
    setEditorState({ ...editorState, [name]: state });
    convertContentToHTML(name);
  };

  const handleChange = (name, value) => {
    setValues({ ...values, [name]: value });
  };

  const handleDynamicChange = (name, value) => {
    setDynamicValues({ ...dynamicValues, [`DE:${name}`]: value });
  };

  const handleMultipleValues = (name, value) => {
    setMultipleValues({ ...multipleValues, [`DE:${name}`]: value });
  };

  const handleDateChange = (name, value) => {
    const newTime = new Date();
    const defaultTime = newTime.setHours(0, 0, 0, 0);
    setDateValues({
      ...dateValues,
      [name]: { date: value, time: dateValues[name]?.time || defaultTime },
    });
  };
  const handleTimeChange = (name, value) => {
    setDateValues({
      ...dateValues,
      [name]: { time: value, date: dateValues[name]?.date },
    });
  };

  const handleChangeSelect = (selectedOption, name) => {
    const selectedArray = [];
    selectedOption.map(optionObject => selectedArray.push(optionObject.value));
    handleMultipleValues(name, selectedArray);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const dateAndTime = dateValues;
    Object.keys(dateValues).forEach(element => {
      dateAndTime[element] = `${format(
        dateValues[element].date,
        'yyyy-MM-dd',
      )}T${format(dateValues[element].time, 'HH:mm:ss')}`;
    });
    const queryParams = {
      ownerID: user,
      queueTopicID: queueId,
      projectID: requestProjectIdValue,
      method: 'post',
      ...dateAndTime,
      ...values,
      ...dynamicValues,
    };

    const actionResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.OPTASK, queryParams },
    );

    if (actionResponse?.ID !== null) {
      setFileComponent(true);
    }

    if (multipleValues) {
      const queryParams2 = {
        method: 'put',
        updates: JSON.stringify(multipleValues),
      };

      await actionWebInvoke(
        actions['workfront-action'],
        {},
        {
          objCode: OBJCODE.OPTASK,
          objID: actionResponse.ID,
          queryParams: queryParams2,
        },
      );
    }
    setIsLoading(false);

    const richTextPromises = Object.keys(convertedContent).map(element =>
      actionWebInvoke(
        actions['workfront-action'],
        {},
        {
          objCode: OBJCODE.OPTASK,
          objID: actionResponse.ID,
          bodyData: `{"DE:${element}":${JSON.stringify(
            convertedContent[element],
          )}}`,
        },
      ),
    );
    await Promise.all(richTextPromises);
    const requestURL = `${actionResponse.workfrontEndpoint}/issue/${actionResponse.ID}/overview`;
    if (actionResponse?.ID !== null) {
      setFileComponent(true);
    }
    window.open(requestURL, '_blank');
  };

  async function getConfigurations() {
    const headers = getHeaders(ims);
    const actionResponse = await actionWebInvoke(
      actions['aio-lib-state-read'],
      headers,
      'key',
    );
    setRequestProjectIdValue(actionResponse.requestProjectId);
  }

  async function fetchQueueTopics() {
    setQueueTopic({ ...queueTopic, isLoading: true });
    const queryParams = {
      'queueDef:projectID': requestProjectIdValue,
    };
    const fields = [
      'queueDef:visibleOpTaskFields',
      'objectCategories:category',
    ];
    const list = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.QUET_SEARCH, queryParams, fields },
    );
    setQueueTopic({
      ...queueTopic,
      elements: list,
      isLoading: false,
    });
  }
  async function selectQueueTopic(selected) {
    setQueueId(selected);
    const selectedQTopicDataFetched = queueTopic.elements.filter(
      item => item.ID === selected,
    );
    setSelectedQTopicData(selectedQTopicDataFetched[0]);
    const data = await fetchCustomFormsAndFields(
      selectedQTopicDataFetched,
      true,
    );
    data.sort((a, b) => {
      return a.displayOrder - b.displayOrder;
    });
    setCustomFields(data.flat());
  }

  useEffect(() => {
    getConfigurations();
  }, [ims]);

  useEffect(() => {
    if (values?.name?.length) {
      setDisabled(false);
    } else setDisabled(true);
  }, [values?.name]);

  useEffect(() => {
    if (requestProjectIdValue.length > 0) {
      fetchQueueTopics();
    }
  }, [requestProjectIdValue]);

  const showFileComponent = loading => {
    return loading ? (
      <Flex justifyContent="center" alignItems="center">
        <ProgressCircle aria-label="loading" isIndeterminate size="L" />
      </Flex>
    ) : (
      <Container>
        <Image
          src="https://i.postimg.cc/zG5m1stD/Screenshot-from-2021-08-27-15-28-48.png"
          alt="File Component"
        />
      </Container>
    );
  };

  return (
    <Wrap>
      {isLoading && isFileComponent ? (
        showFileComponent(isLoading)
      ) : (
        <Container>
          <Title>
            <h3>Submit a request</h3>
            <span>
              <Submit
                top="15px"
                right="10px"
                variant="cta"
                isDisabled={disabled}
                onPress={handleSubmit}
              >
                Submit
              </Submit>
            </span>
          </Title>
          <div>
            <Picker
              width="461px"
              label="Marketing request queue"
              onSelectionChange={selected => selectQueueTopic(selected)}
              defaultSelectedKey="Select Queue Topic..."
              isRequired
            >
              {queueTopic.elements.slice(0).map(row => (
                <Item key={row.ID}>{row.name}</Item>
              ))}
            </Picker>
          </div>
          {queueId ? (
            <TextField
              marginTop="20px"
              isRequired
              name="name"
              label="Title"
              value={values.name || ''}
              onInput={event => {
                const {
                  target: { value },
                } = event;
                handleChange('name', value);
              }}
              placeholder=""
              width="750px"
            />
          ) : (
            ''
          )}

          {selectedQTopicData?.queueDef?.visibleOpTaskFields
            ? selectedQTopicData.queueDef.visibleOpTaskFields.map(
                (field, index) => (
                  <SystemField
                    key={field + index}
                    field={field}
                    values={values}
                    handleChange={handleChange}
                    setDateObject={setDateObject}
                    dateObject={dateObject}
                    handleDateChange={handleDateChange}
                    handleTimeChange={handleTimeChange}
                  />
                ),
              )
            : null}

          {customFields?.map(({ parameter }, index) => {
            return (
              <CustomField
                key={parameter.ID + index}
                parameter={parameter}
                setDateObject={setDateObject}
                handleDateChange={handleDateChange}
                dateObject={dateObject}
                handleTimeChange={handleTimeChange}
                dynamicValues={dynamicValues}
                handleDynamicChange={handleDynamicChange}
                editorState={editorState}
                handleMultipleValues={handleMultipleValues}
                handleChangeSelect={handleChangeSelect}
                handleEditorChange={handleEditorChange}
              />
            );
          })}
        </Container>
      )}
      <Flex direction="column">
        <ExistingBriefs value="Make Project Brief">
          <Button
            top="25px"
            left="15px"
            elementType="a"
            href="#/dynamicbrief"
            variant="primary"
          >
            Start a project
          </Button>
        </ExistingBriefs>
        <RequestProgress user={user} />
      </Flex>
    </Wrap>
  );
};

DynamicRequestForm.propTypes = {
  user: PropTypes.string,
  ims: PropTypes.shape({
    token: PropTypes.string,
    org: PropTypes.string,
  }),
};

DynamicRequestForm.defaultProps = {
  user: '',
  ims: {
    token: '',
    org: '',
  },
};

export default DynamicRequestForm;
